import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import get from 'lodash.get';
import { dateToMidnightUTCObject } from '~/modules/common/dates/convert';
import { getTotalAllocatedHoursStartingFromDate } from '~/modules/resourcing/util';
import { useMeContext } from '~/modules/me';

export const getTotalHoursRemainingAsOfDate = ({
  releaseDateUtc,
  taskAllocationsSummary
}) => {
  if (!taskAllocationsSummary) return null;

  return getTotalAllocatedHoursStartingFromDate({
    date: releaseDateUtc,
    allocation: taskAllocationsSummary
  });
};

export const USER_ASSIGNED_IMPACTED_TASK_SUMMARY_QUERY = gql`
  query GetImpactedTaskDetailsSummary(
    $projectId: String!
    $releaseDate: Date2
    $filter: GetResourceUsersFilter!
    $includeMaxTaskEndDate: Boolean!
  ) {
    resourceUsers2(filter: $filter) {
      id
      displayText
      impactedTaskSummary(projectId: $projectId, releaseDate: $releaseDate) {
        taskUris
        maxTaskEndDate @include(if: $includeMaxTaskEndDate)
        taskAllocationsSummary {
          totalHours
          startDate
          endDate
          scheduleRules {
            dateRange {
              startDate
              endDate
            }
            do
          }
        }
      }
    }
  }
`;

export const IMPACTED_TASK_SUMMARY_FOR_PROJECT_AND_RESOURCE_USER_QUERY = gql`
  query GetImpactedTaskSummaryForProjectAndResourceUser(
    $input: ImpactedTaskSummaryForProjectAndResourceUserInput!
    $includeMaxTaskEndDate: Boolean!
  ) {
    impactedTaskSummaryForProjectAndResourceUser(input: $input) {
      taskUris
      maxTaskEndDate @include(if: $includeMaxTaskEndDate)
      taskAllocationsSummary {
        totalHours
        startDate
        endDate
        scheduleRules {
          dateRange {
            startDate
            endDate
          }
          do
        }
      }
    }
  }
`;

const useLegacyImpactedTaskSummaryForResourceUsers = ({
  projectId,
  userIds,
  releaseDate,
  releaseDateUtc,
  includeMaxTaskEndDate,
  skip
}) => {
  const { data, loading } = useQuery(
    USER_ASSIGNED_IMPACTED_TASK_SUMMARY_QUERY,
    {
      variables: {
        projectId,
        filter: { userIds, includeDisabled: true },
        releaseDate,
        includeMaxTaskEndDate
      },
      skip,
      fetchPolicy: 'network-only'
    }
  );

  const resourceUsers = get(data, 'resourceUsers2', []);

  return {
    usersAssignedImpactedTaskSummary: resourceUsers.map(user => ({
      ...user,
      impactedTaskSummary: {
        ...user.impactedTaskSummary,
        totalHoursRemainingAsOfDate: getTotalHoursRemainingAsOfDate({
          releaseDateUtc,
          taskAllocationsSummary:
            user.impactedTaskSummary?.taskAllocationsSummary
        })
      }
    })),
    isLoading: loading
  };
};

const useImpactedTaskSummaryForResourceUser = ({
  projectId,
  userId,
  userDisplayText,
  releaseDate,
  releaseDateUtc,
  includeMaxTaskEndDate,
  skip
}) => {
  const { data, loading } = useQuery(
    IMPACTED_TASK_SUMMARY_FOR_PROJECT_AND_RESOURCE_USER_QUERY,
    {
      variables: {
        input: { userId, projectId, releaseDate },
        includeMaxTaskEndDate
      },
      skip,
      fetchPolicy: 'network-only'
    }
  );

  const impactedTaskSummary =
    data?.impactedTaskSummaryForProjectAndResourceUser;

  const userAssignedImpactedTaskSummary = impactedTaskSummary
    ? {
        id: userId,
        displayText: userDisplayText,
        impactedTaskSummary: {
          ...impactedTaskSummary,
          totalHoursRemainingAsOfDate: getTotalHoursRemainingAsOfDate({
            releaseDateUtc,
            taskAllocationsSummary: impactedTaskSummary?.taskAllocationsSummary
          })
        }
      }
    : null;

  return {
    userAssignedImpactedTaskSummary,
    isLoading: loading
  };
};

const useGetUsersAssignedImpactedTaskSummary = ({
  includeMaxTaskEndDate = false,
  projectId,
  userIds,
  userDisplayText,
  asOfDate,
  skip = false
}) => {
  const {
    featureFlags: { isPsaRmpEnhancedProjectManagerPersonaEnabled }
  } = useMeContext();

  const releaseDateUtc = asOfDate
    ? dateToMidnightUTCObject(asOfDate).plus({ days: 1 })
    : null;
  const releaseDate = releaseDateUtc?.toISO();

  const resourceUsersLegacyResult = useLegacyImpactedTaskSummaryForResourceUsers(
    {
      projectId,
      userIds,
      releaseDate,
      releaseDateUtc,
      includeMaxTaskEndDate,
      skip: skip || isPsaRmpEnhancedProjectManagerPersonaEnabled
    }
  );

  const {
    userAssignedImpactedTaskSummary,
    isLoading
  } = useImpactedTaskSummaryForResourceUser({
    projectId,
    userId: userIds[0],
    userDisplayText,
    releaseDate,
    releaseDateUtc,
    includeMaxTaskEndDate,
    skip: skip || !isPsaRmpEnhancedProjectManagerPersonaEnabled
  });

  return isPsaRmpEnhancedProjectManagerPersonaEnabled
    ? {
        usersAssignedImpactedTaskSummary: userAssignedImpactedTaskSummary
          ? [userAssignedImpactedTaskSummary]
          : [],
        isLoading
      }
    : resourceUsersLegacyResult;
};

export default useGetUsersAssignedImpactedTaskSummary;
